import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOSchema from '@/components/base/SEOSchema';
import Breadcrumb from '@/components/base/Breadcrumb';
import { features, mediaPraise, teamMembers, platformStats, brandStory } from '@/mocks/comics';

export default function AboutPage() {
  const aboutSchema = [
    {
      '@context': 'https://schema.org',
      '@type': 'AboutPage',
      name: '关于动漫之家 - 韩漫在线免费阅读平台',
      description: brandStory.description1,
      url: 'http://hgok7.com/about',
    },
    {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: '动漫之家',
      url: 'http://hgok7.com/',
      description: brandStory.description1,
      foundingDate: '2022',
      address: {
        '@type': 'PostalAddress',
        streetAddress: '望京SOHO T3栋28层2806室',
        addressLocality: '北京',
        addressRegion: '朝阳区',
        addressCountry: 'CN',
      },
      contactPoint: {
        '@type': 'ContactPoint',
        telephone: '+86-400-888-6666',
        contactType: 'customer service',
      },
      sameAs: ['http://hgok7.com/'],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: 'http://hgok7.com/' },
        { '@type': 'ListItem', position: 2, name: '关于我们' },
      ],
    },
  ];

  return (
    <>
      <SEOSchema
        schemas={aboutSchema}
        canonicalUrl="http://hgok7.com/about"
        pageTitle="关于动漫之家 - 韩漫在线免费阅读平台介绍"
        pageDescription="动漫之家专注无删减韩国漫画下拉式呈现，成立于2022年，致力于为韩漫爱好者打造优质免费阅读平台。了解我们的品牌故事、团队及联系方式。"
        pageKeywords="关于动漫之家,韩漫平台介绍,动漫之家品牌,韩国漫画阅读平台,联系我们"
      />

      <Navbar />

      <main>
        {/* Brand Story */}
        <article className="pt-24 pb-16 md:pb-24 px-6 md:px-14 lg:px-20 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <div className="mb-6">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: '关于我们' },
                ]}
              />
            </div>

            <div className="flex flex-col lg:flex-row gap-10 lg:gap-0" style={{ width: '100%' }}>
              <div className="lg:w-[5fr]">
                <div className="w-full aspect-[4/3] rounded-3xl overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=Modern%20creative%20office%20space%20with%20team%20collaborating%2C%20large%20monitors%20displaying%20webtoon%20art%2C%20warm%20natural%20light%20from%20large%20windows%2C%20comfortable%20workspace%2C%20creative%20atmosphere%2C%20professional%20yet%20relaxed%20environment%2C%20clean%20minimal%20interior%2C%20plants%20and%20wood%20accents&width=800&height=600&seq=about-brand-001&orientation=landscape"
                    alt="动漫之家办公环境"
                    title="动漫之家 - 韩漫在线免费阅读平台"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              <div className="lg:w-[5fr] lg:pl-12 flex flex-col justify-center">
                <span className="inline-block px-4 py-1.5 rounded-full bg-accent-100 text-accent-900 text-xs font-label font-semibold mb-4 w-fit">
                  关于动漫之家
                </span>
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-foreground-950 leading-tight mb-6">
                  {brandStory.subtitle}
                </h1>
                <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                  {brandStory.description1}
                </p>
                <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                  {brandStory.description2}
                </p>
                <p className="text-sm text-foreground-600 leading-relaxed mb-6">
                  {brandStory.description3}
                </p>
                <span className="text-xs text-foreground-400">{brandStory.founded}</span>
              </div>
            </div>
          </div>
        </article>

        {/* Platform Stats */}
        <section className="bg-background-50 py-16 md:py-24 px-6 md:px-14 lg:px-20">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {platformStats.map((stat) => (
                <div key={stat.label} className="text-center p-6 rounded-2xl bg-background-100">
                  <div className="w-12 h-12 flex items-center justify-center mx-auto mb-3 rounded-xl bg-primary-100">
                    <i className={`${stat.icon} text-xl text-primary-500`}></i>
                  </div>
                  <p className="text-3xl md:text-4xl font-heading font-bold text-foreground-950">{stat.value}</p>
                  <p className="text-xs text-foreground-400 mt-1">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="bg-background-100 py-16 md:py-24 px-6 md:px-14 lg:px-20">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-4xl md:text-5xl font-heading font-bold text-foreground-950 text-center mb-14">
              平台优势
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {features.map((f, idx) => (
                <div key={idx} className="bg-background-50 rounded-2xl p-8 md:p-10">
                  <div className="w-12 h-12 flex items-center justify-center mb-6">
                    <i className={`${f.icon} text-3xl text-foreground-950`}></i>
                  </div>
                  <h3 className="text-xl font-heading font-bold text-foreground-950 mb-3">{f.title}</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">{f.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Team */}
        <section className="bg-background-50 py-16 md:py-24 px-6 md:px-14 lg:px-20">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-4xl md:text-5xl font-heading font-bold text-foreground-950 text-center mb-14">
              核心团队
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {teamMembers.map((member) => (
                <div key={member.name} className="bg-background-100 rounded-2xl overflow-hidden">
                  <div className="aspect-[4/5] overflow-hidden">
                    <img
                      src={member.image}
                      alt={member.name}
                      title={`${member.name} - ${member.role}`}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                  <div className="p-6">
                    <h3 className="text-lg font-heading font-bold text-foreground-950">{member.name}</h3>
                    <p className="text-xs text-primary-500 font-label font-semibold mt-1 mb-3">{member.role}</p>
                    <p className="text-sm text-foreground-600 leading-relaxed">{member.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Media Praise */}
        <section className="bg-background-100 py-16 md:py-24 px-6 md:px-14 lg:px-20">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col lg:flex-row gap-10 lg:gap-0" style={{ width: '100%' }}>
              <div className="lg:w-[6fr] pr-0 lg:pr-16">
                <div className="flex items-center gap-2 mb-8">
                  <span className="w-2 h-2 rounded-full bg-primary-500"></span>
                  <h2 className="text-xs font-label font-semibold text-foreground-500 tracking-widest uppercase">
                    媒体口碑
                  </h2>
                </div>

                {mediaPraise.map((mp, idx) => (
                  <blockquote
                    key={idx}
                    className={`${idx > 0 ? 'mt-10 pt-10 border-t border-background-300' : ''}`}
                  >
                    <p className="text-xl md:text-2xl font-heading font-medium text-foreground-900 leading-relaxed italic">
                      {mp.quote}
                    </p>
                    <footer className="mt-5">
                      <span className="text-sm font-label font-bold text-foreground-950">
                        —— {mp.source}
                      </span>
                    </footer>
                  </blockquote>
                ))}
              </div>

              <div className="lg:w-[4fr] flex items-center justify-center">
                <div className="w-full h-[350px] rounded-3xl overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=Korean%20webtoon%20art%20display%20on%20digital%20screen%2C%20colorful%20comic%20panels%2C%20modern%20minimal%20display%20setting%2C%20clean%20gallery%20aesthetic%2C%20professional%20art%20showcase%2C%20soft%20ambient%20lighting%2C%20creative%20atmosphere&width=600&height=700&seq=about-media-001&orientation=portrait"
                    alt="媒体评价"
                    title="动漫之家媒体口碑"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Contact CTA */}
        <section
          className="relative h-[60vh] w-full overflow-hidden"
          aria-label="联系与合作"
        >
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=Warm%20professional%20office%20meeting%20space%2C%20collaborative%20atmosphere%2C%20sunlight%20through%20large%20windows%2C%20modern%20minimal%20furniture%2C%20creative%20environment%2C%20golden%20hour%20lighting%2C%20inviting%20professional%20setting%2C%20clean%20aesthetic&width=1920&height=800&seq=cta-about-001&orientation=landscape"
              alt="商务合作联系"
              title="动漫之家商务合作"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/60"></div>
          </div>

          <div className="relative z-10 h-full flex flex-col items-center justify-center text-center px-6">
            <h2 className="text-3xl md:text-5xl lg:text-6xl font-heading font-semibold text-white mb-4">
              加入我们 / 商务合作
            </h2>
            <p className="text-sm md:text-base text-white/70 font-body max-w-md leading-relaxed mb-8">
              欢迎漫画创作者、出版社及商务伙伴与我们联系
              <br />
              共同推动韩漫文化在中国的传播与发展
            </p>
            <a
              href="mailto:hello@hgok7.com"
              className="whitespace-nowrap inline-flex items-center gap-3 px-8 py-3.5 rounded-full bg-foreground-950 text-background-50 text-sm font-label font-semibold hover:bg-foreground-800 transition-all duration-200 cursor-pointer"
            >
              <span className="w-7 h-7 flex items-center justify-center rounded-full bg-primary-500">
                <i className="ri-mail-line text-background-50 text-xs"></i>
              </span>
              发送邮件
              <i className="ri-arrow-right-up-line"></i>
            </a>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}